/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_einvernehmliche-Scheidung_700px.jpg";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Box, Link as MuiLink, Paper } from "@mui/material";
import MuiListNativeStyled from "../../List/MuiListNativeStyled";

const GuidebookAgreedDivorce = () => {
  const title = "Ratgeber - Einvernehmliche Scheidung | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Welche Vorteile bringt eine einvernehmliche Scheidung mit sich und welche Themen sollte ich mit meinem Partner abklären?"
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Einfach erklärt: Einvernehmliche Scheidung"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Welche Vorteile bringt eine einvernehmliche Scheidung mit sich, wann handelt es sich um eine einvernehmliche Scheidung und welche Themen sollte ich
            mit meinem Partner abklären? Wir geben Ihnen einen Überblick über die einvernehmliche Scheidung.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Vorteile einer einvernehmlichen Scheidung
          </Typography>
          <p>
            Bei einer einvernehmlichen Scheidung einigen sich die Ehepartner im Voraus über die Scheidung und die damit zusammenhängenden Themen. Neben dem
            Vermeiden von unnötigen Streitigkeiten, die die Ehepartner und die Familie sehr belasten können, ist der große Vorteil einer einvernehmlichen
            Scheidung, dass sie viel Geld, Zeit und Aufwand spart.
          </p>
          <Typography variant={"h5"}>1. Geld sparen</Typography>
          <p>
            Da keine Streitpunkte vor Gericht geregelt werden müssen, benötigen nicht beide Ehepartner je einen Anwalt, sondern nur der Antragsteller muss einen
            Anwalt beauftragen, der dann die Scheidung durchführt. Die hohen Kosten für einen zusätzlichen Anwalt des Ehepartners können somit vollständig
            eingespart werden.
          </p>
          <Typography variant={"h5"}>2. Zeit sparen</Typography>
          <p>
            Auch zeitlich gesehen ist die einvernehmliche Scheidung die beste Option. Eine streitige Scheidung kann mehrere Jahre andauern, eine einvernehmliche
            Scheidung dauert hingegen meist nur zwischen drei und sechs Monaten. Mehr zur Dauer einer Scheidung erfahren Sie{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/scheidungsdauer"}>
              hier
            </MuiLink>
            .
          </p>
          <Typography variant={"h5"}>3. Aufwand minimieren</Typography>
          <p>
            Auch der Organisationsaufwand ist bei einer einvernehmlichen Scheidung deutlich geringer. Anstatt mehrerer Besuche beim Anwalt und umständlichen
            Verschicken von Dokumenten per Post können Sie jetzt direkt mit Ihrer Scheidung starten und alles online erledigen. In der Servicewelt von Legalbird
            können Sie ganz unkompliziert alle Angaben für den Scheidungsantrag eingegeben, Ihre individuellen Kosten berechnen und Dokumente sicher hochladen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Voraussetzungen für eine einvernehmliche Scheidung - Trennungsjahr und keine Streitpunkte
          </Typography>
          <p>
            Auch bei einer einvernehmlichen Scheidung gilt die Grundvoraussetzung, dass die Ehepartner seit einem Jahr getrennt voneinander leben - dies geht
            auch im selben Wohnraum, solange Sie sich kein Bett mehr teilen. Das Trennungsjahr ist gesetzlich vorgeschrieben, um sich offiziell scheiden lassen
            zu können. So soll sicher gegangen werden, dass die Ehe unwiderruflich gescheitert ist.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Keine Streitigkeiten mit Ihrem Partner
          </Typography>
          <p>
            Wenn Sie sich einvernehmlich scheiden lassen möchten, müssen Sie offen und in Ruhe verschiedene Themen mit Ihrem Partner abklären und diese
            bestenfalls schriftlich festhalten. Dies erleichtert den Scheidungsprozess und verbessert auch die Stimmung zwischen Ihnen und Ihrem Partner. Denn
            neben dem gerichtlichen Prozess löst eine Einreichung der Scheidung eine Reihe von Folgen aus, die Sie abklären müssen. Nur wenn Sie sich mit Ihrem
            Partner einig sind, handelt es sich um eine einvernehmliche Scheidung. Themen, die eine Scheidung zusätzlich zum eigentlichen Scheidungsprozess mit
            sich bringt, werden unter dem Begriff Scheidungsfolgesachen zusammengefasst.
          </p>
          <HighlightBox headline={"Was sind Scheidungsfolgesachen?"}>
            <Typography sx={guidebookContentStyle.listIntroText}>Über folgende Themen müssen Sie sich mit Ihrem Partner austauschen und einigen:</Typography>
            <MuiListNativeStyled
              sx={guidebookContentStyle.bulletList}
              listItems={["Ehegattenunterhalt", "Verteilung von Besitz/Hausrat und ehelicher Wohnung"]}
            />
            <Typography sx={guidebookContentStyle.listIntroText}>Wenn Sie gemeinsame, minderjährige Kinder haben, muss zusätzlich geklärt werden:</Typography>
            <MuiListNativeStyled sx={guidebookContentStyle.bulletList} listItems={["Sorgerecht", "Umgangsrecht", "Kindesunterhalt"]} />
            <Box sx={guidebookContentStyle.centered}>
              Tipp: Sie finden im{" "}
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht"}>
                Ratgeber
              </MuiLink>{" "}
              zu all diesen Themen detaillierte Spezialartikel
            </Box>
          </HighlightBox>
          <p>
            Für Scheidungsfolgesachen ist keine Klärung vor Gericht nötig, wenn Sie sich mit Ihrem Partner einig werden. Wenn Sie sich bei bestimmten
            Folgesachen nicht einigen können, muss ein Partner zur Klärung das Gericht beauftragen und die Scheidung wird von einer einvernehmlichen zu einer
            streitigen Scheidung, was sehr teuer und langwierig werden kann. Daher ist es sehr empfehlenswert, alles direkt mit dem Partner abzuklären. Wenn Sie
            sich über die streitigen Themen geeinigt haben, sollten Sie alles in einer Scheidungsfolgenvereinbarung schriftlich festhalten und diese ggf. auch
            notariell beglaubigen lassen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Alles Wichtige zur Scheidungsfolgenvereinbarung
          </Typography>
          <p>
            Durch eine Scheidungsfolgenvereinbarung legen die Ehepartner Regeln fest, die nach dem Scheidungsverfahren und nach der rechtskräftigen Scheidung
            gelten sollen. Dies ermöglicht eine reibungslose und offene Klärung der verschiedenen Themen und verhindert, dass es zu einer sehr teuren,
            streitigen Scheidung vor Gericht kommt. Die Vereinbarung sollte notariell beurkundet und ggf. zusammen mit einem Anwalt oder Notar angefertigt
            werden, um unzulässige Klauseln zu vermeiden und ein rechtskräftiges Dokument zu erstellen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Kostenlose Vorlage
          </Typography>
          <p>
            Sie können sich hier ein Muster für eine Scheidungsfolgenvereinbarung herunterladen. So erhalten Sie einen ersten Eindruck über das Format und
            mögliche Inhalte. Sie müssen dieses Muster natürlich noch auf Ihre individuellen Ansprüche anpassen und von einem Notar beurkunden lassen.
          </p>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Button
                component={"a"}
                href={"/documents/Legalbird_Vorlage_Scheidungsfolgenvereinbarung.pdf"}
                download={"Scheidungsfolgenvereinbarung"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Download pdf
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                component={"a"}
                href={"/documents/Legalbird_Vorlage_Scheidungsfolgenvereinbarung.docx"}
                download={"Scheidungsfolgenvereinbarung"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Download Word Datei
              </Button>
            </Grid>
          </Grid>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Ihre Scheidung
          </Typography>
          <p>
            Abgesehen von der Klärung der Scheidungsfolgen muss natürlich der Scheidungsantrag bei Gericht eingereicht werden. Sie können ganz einfach alle
            Angaben für den Antrag direkt online eingeben, wir erstellen den Scheidungsantrag und reichen ihn bei Gericht für Sie ein.
          </p>
          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookAgreedDivorce;
